import React, { useMemo, useState } from 'react';

import { makeStyles } from 'tss-react/mui';

import { LucideIconPicker } from './LucideIconPicker';
import { LucideIconRenderer } from './LucideIconRenderer';
import type { LucideIcon } from './useLucideIconPicker';

interface StyleProps {
  isOpen: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, { isOpen }) => ({
  button: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    alignItems: 'center',
    backgroundColor: isOpen ? theme.palette.action.hover : undefined,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1.25),
  },
}));

interface LucideIconButtonProps {
  onChange: (iconName: string) => void;
  value: string;
  className?: string;
  customIcons?: LucideIcon[];
  onColorChange?: (color: string) => void;
  selectedColor?: string;
  size?: number;
}

export function LucideIconButton({
  className,
  customIcons,
  onChange,
  onColorChange,
  selectedColor,
  size = 24,
  value,
}: LucideIconButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const { classes, cx } = useStyles({ isOpen: open });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const currentIconUrl = useMemo(() => {
    return customIcons?.find(icon => icon.name === value)?.url;
  }, [customIcons, value]);

  return (
    <>
      <div
        ref={setAnchorEl}
        className={cx(classes.button, className)}
        onClick={() => setOpen(true)}
      >
        <LucideIconRenderer
          color={selectedColor}
          icon={value}
          iconUrl={currentIconUrl}
          size={size}
        />
      </div>

      <LucideIconPicker
        anchorEl={anchorEl}
        customIcons={customIcons}
        open={open}
        selectedColor={selectedColor}
        value={value}
        onClose={() => setOpen(false)}
        onColorSelect={onColorChange}
        onSelect={iconName => {
          onChange(iconName);
          setOpen(false);
        }}
      />
    </>
  );
}
